import {
  memo, useState, useCallback, useMemo,
} from 'react';
import {
  Divider,
  Grid,
  PanelContent,
  SearchableTextField,
  useTranslations,
  Button,
  PlusIcon,
  Tooltip,
  FilterButton,
  useMapKeyValueExtractor,
  useSnackbar,
  ActionButton,
  ErrorIcon,
  PromptModal,
  usePubSub,
  usePopper,
  MoveIcon,
  ShareIcon,
  Typography,
  encryptSymmetric,
  ISearchableTextFieldProps,
  IFilterButtonProps,
} from '@uniqkey-frontend/shared-app';
import { EmployeeAccountStatus, Ownership } from '@uniqkey-backend-organization-web/api-client';
import EmployeeCreditCardsTable from '../../../../components/tables/EmployeeCreditCardsTable';
import useEmployeeGroupCreditCardsTable, { IEmployeeGroupCreditCardsTableRow } from
  '../../../../hooks/tables/useEmployeeGroupCreditCardsTable';
import EmployeeCreditCardsTabFilter, {
  IEmployeeCreditCardsTabFilterSubmitResult,
} from './components/EmployeeCreditCardsTabFilter';
import {
  useGetEmployeeAccountById,
  useDeleteVaultsToEmployeeAccounts,
} from '../../../../hooks/reactQuery';
import PubSubEventEnum from '../../../../enums/PubSubEventEnum';
import { logException } from '../../../../services/sentryService';
import { getActiveOrganizationId } from '../../../../services/organizationService';
import { useTrustedPortalStore } from '../../../../modules/TrustedPortalModule/store';
import useVaultsAPI from '../../../../hooks/useVaultsAPI';
import CreateCreditCardModal, {
  ICreateCreditCardReturnValue,
} from '../../../../components/CreateCreditCardModal';
import useVaultsToEmployeeAccountsAPI from '../../../../hooks/useVaultsToEmployeeAccountsAPI';
import { useCompanionApplicationContext } from '../../../../contexts/CompanionApplicationContext';
import { generateTooltipTitle } from '../../../../helpers/tooltips';
import ShareVaultsToGroupModal from '../../../../components/ShareVaultsToGroupModal';
import VaultTypeEnum from '../../../../enums/VaultTypeEnum';

interface IEmployeeCreditCardsTabProps {
  employeeAccountId: string;
  employeeAccountStatus: EmployeeAccountStatus;
}

const EmployeeCreditCardsTab = (props: IEmployeeCreditCardsTabProps) => {
  const { employeeAccountId, employeeAccountStatus } = props;
  const { t } = useTranslations();
  const { showError, showWarning, showSuccess } = useSnackbar();
  const { symmetricKey } = useCompanionApplicationContext();
  const { createVaultsCreditCardToEmployeeAccounts } = useVaultsToEmployeeAccountsAPI();
  const { moveVaultsToPrivateKeychain } = useVaultsAPI();
  const { mutate: mutateDeleteCreditCard } = useDeleteVaultsToEmployeeAccounts();
  const [isDeleteCreditCardModalOpen, setIsDeleteCreditCardModalOpen] = useState<boolean>(false);
  const [isDeleteCreditCardLoading, setIsDeleteCreditCardLoading] = useState<boolean>(false);
  const [isMoveCreditCardsModalOpen, setIsMoveCreditCardsModalOpen] = useState<boolean>(false);
  const [isMoveCreditCardsLoading, setIsMoveCreditCardsLoading] = useState<boolean>(false);
  const [isShareCreditCardsModalOpen, setIsShareCreditCardsModalOpen] = useState<boolean>(false);
  const [isCreateCreditCardModalOpen, setIsCreateCreditCardModalOpen] = useState<boolean>(false);
  const [isCreateCreditCardLoading, setIsCreateCreditCardLoading] = useState<boolean>(false);

  const handleDeleteCreditCardModalOpen = useCallback(
    () => setIsDeleteCreditCardModalOpen(true),
    [],
  );
  const handleDeleteCreditCardModalClose = useCallback(
    () => setIsDeleteCreditCardModalOpen(false),
    [],
  );
  const handleMoveCreditCardsModalOpen = useCallback(() => setIsMoveCreditCardsModalOpen(true), []);
  const handleMoveCreditCardsModalClose = useCallback(
    () => setIsMoveCreditCardsModalOpen(false),
    [],
  );
  const handleShareCreditCardsModalOpen = useCallback(
    () => setIsShareCreditCardsModalOpen(true),
    [],
  );
  const handleShareCreditCardsModalClose = useCallback(
    () => setIsShareCreditCardsModalOpen(false),
    [],
  );
  const handleCreateCreditCardModalOpen = useCallback(
    () => setIsCreateCreditCardModalOpen(true),
    [],
  );
  const handleCreateCreditCardModalClose = useCallback(
    () => setIsCreateCreditCardModalOpen(false),
    [],
  );

  const {
    isOpen: isFilterOpen,
    anchorEl: filterAnchorEl,
    setPopperIsOpen: setIsFilterOpen,
  } = usePopper();
  const toggleIsFilterOpen = useCallback<NonNullable<IFilterButtonProps['onChange']>>(
    (event) => setIsFilterOpen(!isFilterOpen, event),
    [setIsFilterOpen, isFilterOpen],
  );

  const {
    selectedCreditCards,
    refetch,
    resetSelectedRows,
    resetActivePage,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    ...restTableProps
  } = useEmployeeGroupCreditCardsTable({
    persistentFilters: { employeeAccountId },
    noDataMessageKey: 'creditCardsTab.table.noData',
  });

  const handleFilterClose = useCallback(() => setIsFilterOpen(false), [setIsFilterOpen]);

  const handleFilterSubmit = useCallback((
    updatedValues: IEmployeeCreditCardsTabFilterSubmitResult,
  ) => {
    setFilterValues(updatedValues);
    resetActivePage();
  }, [setFilterValues, resetActivePage]);

  const { data: employeeAccount } = useGetEmployeeAccountById(
    { employeeAccountId },
  );

  const {
    values: selectedCreditCardsAsObjects, keys: selectedCreditCardsIds,
  } = useMapKeyValueExtractor<IEmployeeGroupCreditCardsTableRow>(selectedCreditCards);

  const handleRefetch = useCallback(() => refetch(true), [refetch]);
  usePubSub(PubSubEventEnum.DATA_SYNCHRONIZATION_VAULT, handleRefetch);

  const activeOrganizationId = getActiveOrganizationId();
  const isTrustedPortalEnabled = useTrustedPortalStore.useIsEnabledByOrganizationId()[
    activeOrganizationId!
  ] ?? false;

  const {
    isMoveDisabled, isShareDisabled, isRemoveDisabled,
  } = useMemo(() => {
    if (!selectedCreditCardsAsObjects.length) {
      return { isMoveDisabled: true, isShareDisabled: true, isRemoveDisabled: true };
    }
    let disableMove = false;
    let disableShare = false;
    let disableRemove = false;
    selectedCreditCardsAsObjects.forEach(({ ownership }) => {
      if (ownership === Ownership.Groups) {
        disableMove = true;
        disableRemove = true;
      }
      if (ownership === Ownership.Employees) {
        disableShare = true;
      }
    });
    return {
      isMoveDisabled: disableMove,
      isShareDisabled: disableShare,
      isRemoveDisabled: disableRemove,
    };
  }, [selectedCreditCardsAsObjects]);

  const handleDeleteCreditCard = useCallback(async () => {
    setIsDeleteCreditCardLoading(true);
    mutateDeleteCreditCard({
      vaultIds: selectedCreditCardsIds, employeeAccountId,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t('deleteCreditCardModal.successMessage', { count: successCount }),
          });
        }
        if (failCount) {
          showError({
            text: t('deleteCreditCardModal.errorMessage', { count: failCount }),
          });
        }
        handleDeleteCreditCardModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: (e) => {
        showError({ text: t('common.somethingWentWrong') });
        logException(e, { message: 'EmployeeCreditCardsTab/handleDeleteCreditCard exception' });
      },
      onSettled: () => setIsDeleteCreditCardLoading(false),
    });
  }, [
    employeeAccountId,
    selectedCreditCardsIds,
    mutateDeleteCreditCard,
    handleDeleteCreditCardModalClose,
    resetSelectedRows,
    resetActivePage,
    showError,
    showSuccess,
    t,
  ]);

  const handleMoveCreditCards = useCallback(async () => {
    try {
      setIsMoveCreditCardsLoading(true);
      await moveVaultsToPrivateKeychain({ vaultIds: selectedCreditCardsIds, employeeAccountId });
      if (isTrustedPortalEnabled) {
        showSuccess({
          text: t('trustedPortalSuccessNotifications.creditCard.moved'),
        });
      } else {
        showWarning({
          text: t('moveCreditCardsModal.approvalOnMobileToast'),
        });
      }
      resetSelectedRows();
      resetActivePage();
      handleMoveCreditCardsModalClose();
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'EmployeeCreditCardsTab/handleMoveCreditCards exception',
      });
    } finally {
      setIsMoveCreditCardsLoading(false);
    }
  }, [
    moveVaultsToPrivateKeychain,
    selectedCreditCardsIds,
    handleMoveCreditCardsModalClose,
    employeeAccountId,
    resetSelectedRows,
    resetActivePage,
    showError,
    showWarning,
    showSuccess,
    isTrustedPortalEnabled,
    t,
  ]);

  const handleCreateCreditCard = useCallback(async (params: ICreateCreditCardReturnValue) => {
    const {
      name, cardNumber, expiryDate, cvv, cardHolderName,
    } = params;
    setIsCreateCreditCardLoading(true);
    try {
      await createVaultsCreditCardToEmployeeAccounts({
        employeeAccountId,
        name,
        cardNumber: encryptSymmetric({
          key: symmetricKey,
          string: cardNumber,
        }),
        expiryDate: encryptSymmetric({
          key: symmetricKey,
          string: expiryDate,
          fallbackValue: '',
        }),
        cardHolderName: encryptSymmetric({
          key: symmetricKey,
          string: cardHolderName,
          fallbackValue: '',
        }),
        cvv: encryptSymmetric({
          key: symmetricKey,
          string: cvv,
          fallbackValue: '',
        }),
      });
      if (isTrustedPortalEnabled) {
        showSuccess({
          text: t('trustedPortalSuccessNotifications.creditCard.created'),
        });
      } else {
        showWarning({
          text: t('createCreditCardModal.approvalOnMobileToast'),
        });
      }
      handleCreateCreditCardModalClose();
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'EmployeeCreditCardsTab/handleCreateCreditCard exception',
      });
    } finally {
      setIsCreateCreditCardLoading(false);
    }
  }, [
    createVaultsCreditCardToEmployeeAccounts,
    handleCreateCreditCardModalClose,
    employeeAccountId,
    symmetricKey,
    isTrustedPortalEnabled,
    showSuccess,
    showWarning,
    showError,
    t,
  ]);

  const handleSearchChange = useCallback<ISearchableTextFieldProps['onChange']>(
    (debouncedValue) => {
      setSearchQuery(debouncedValue);
      resetActivePage();
    },
    [setSearchQuery, resetActivePage],
  );

  const moveCreditCardsModalDescriptionElement = useMemo(
    () => (
      <>
        <Typography component="span">
          {t('moveCreditCardsModal.description', { email: employeeAccount?.email })}
        </Typography>
        <Typography component="span">{t('moveCreditCardsModal.additionalDescription')}</Typography>
      </>
    ),
    [employeeAccount?.email, t],
  );

  const { moveTooltipTitle, shareTooltipTitle, removeTooltipTitle } = useMemo(() => {
    const moveTitle = generateTooltipTitle({
      selectedDataLength: selectedCreditCardsAsObjects.length,
      t,
      isDisabled: isMoveDisabled,
      key: 'creditCardsTab.move',
    });
    const shareTitle = generateTooltipTitle({
      selectedDataLength: selectedCreditCardsAsObjects.length,
      t,
      isDisabled: isShareDisabled,
      key: 'creditCardsTab.share',
    });
    const removeTitle = generateTooltipTitle({
      selectedDataLength: selectedCreditCardsAsObjects.length,
      t,
      isDisabled: isRemoveDisabled,
      key: 'creditCardsTab.remove',
    });

    return {
      moveTooltipTitle: moveTitle,
      shareTooltipTitle: shareTitle,
      removeTooltipTitle: removeTitle,
    };
  }, [selectedCreditCardsAsObjects.length, t, isMoveDisabled, isShareDisabled, isRemoveDisabled]);

  const isCreateCreditCardShown = useMemo(() => (
    employeeAccountStatus !== EmployeeAccountStatus.Archived
  ), [employeeAccountStatus]);

  const isMoveCreditCardShown = useMemo(() => (
    employeeAccountStatus !== EmployeeAccountStatus.Archived
    && employeeAccountStatus !== EmployeeAccountStatus.Staged
    && employeeAccountStatus !== EmployeeAccountStatus.Invited
  ), [employeeAccountStatus]);

  return (
    <PanelContent p={0}>
      <Grid container justifyContent="space-between" alignItems="stretch" p={1}>
        <Grid item xs={4} container flexWrap="nowrap" spacing={1}>
          <Grid item>
            <Tooltip title={t('common.filter')}>
              <FilterButton
                isFilterActive={isFilterActive}
                numberOfActiveFilters={numberOfActiveFilters}
                selected={isFilterOpen}
                onChange={toggleIsFilterOpen}
              />
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
          {isMoveCreditCardShown && (
            <Grid item alignSelf="center">
              <Tooltip title={moveTooltipTitle}>
                <ActionButton
                  width={40}
                  height={40}
                  onClick={handleMoveCreditCardsModalOpen}
                  disabled={isMoveDisabled}
                >
                  <MoveIcon />
                </ActionButton>
              </Tooltip>
            </Grid>
          )}
          <Grid item alignSelf="center">
            <Tooltip title={shareTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleShareCreditCardsModalOpen}
                disabled={isShareDisabled}
              >
                <ShareIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={removeTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDeleteCreditCardModalOpen}
                disabled={isRemoveDisabled}
              >
                <ErrorIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
        </Grid>
        <Grid item xs={8} container justifyContent="flex-end" flexWrap="nowrap">
          <Grid item>
            <SearchableTextField
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('common.search')}
            />
          </Grid>
          {isCreateCreditCardShown && (
            <Grid item ml={3}>
              <Button
                icon={<PlusIcon />}
                onClick={handleCreateCreditCardModalOpen}
              >
                {t('creditCardsTab.createCreditCardButton')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Divider />
      <EmployeeCreditCardsTable
        selectedCreditCards={selectedCreditCards}
        {...restTableProps}
      />
      <EmployeeCreditCardsTabFilter
        isOpen={isFilterOpen}
        anchorEl={filterAnchorEl}
        onSubmit={handleFilterSubmit}
        onClose={handleFilterClose}
        initialValues={filterValues}
      />
      {isDeleteCreditCardModalOpen && (
        <PromptModal
          open={isDeleteCreditCardModalOpen}
          onClose={handleDeleteCreditCardModalClose}
          onSubmit={handleDeleteCreditCard}
          title={t(
            'deleteCreditCardModal.deleteFromEmployee.title',
            { count: selectedCreditCards.size },
          )}
          description={t(
            'deleteCreditCardModal.deleteFromEmployee.description',
            { email: employeeAccount?.email },
          )}
          additionalDescription={t(
            'deleteCreditCardModal.deleteFromEmployee.additionalDescription',
          )}
          list={selectedCreditCardsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDeleteCreditCardLoading}
        />
      )}
      {isMoveCreditCardsModalOpen && (
        <PromptModal
          open={isMoveCreditCardsModalOpen}
          onClose={handleMoveCreditCardsModalClose}
          onSubmit={handleMoveCreditCards}
          title={t('moveCreditCardsModal.title', { count: selectedCreditCards.size })}
          description={moveCreditCardsModalDescriptionElement}
          list={selectedCreditCardsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isMoveCreditCardsLoading}
          approvalButtonText="common.move"
          approvalButtonColor="primary"
        />
      )}
      {isShareCreditCardsModalOpen && (
        <ShareVaultsToGroupModal
          isOpen={isShareCreditCardsModalOpen}
          onClose={handleShareCreditCardsModalClose}
          vaults={selectedCreditCardsAsObjects}
          type={VaultTypeEnum.CreditCard}
          onSubmit={resetSelectedRows}
        />
      )}
      {isCreateCreditCardModalOpen && (
        <CreateCreditCardModal
          isOpen={isCreateCreditCardModalOpen}
          onClose={handleCreateCreditCardModalClose}
          onSubmit={handleCreateCreditCard}
          isLoading={isCreateCreditCardLoading}
        />
      )}
    </PanelContent>
  );
};

export default memo(EmployeeCreditCardsTab);
